import React, { useState, useEffect, useRef } from 'react';
import util from '../../styles/utilities.module.css';
import './ModuleQuiz.css';

const ModuleQuiz = ({ 
  quizData, 
  moduleId, 
  progress, 
  onQuizSubmit, 
  onQuizComplete 
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [timeSpent, setTimeSpent] = useState({});
  const [startTime, setStartTime] = useState(Date.now());
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [showResults, setShowResults] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questions = quizData?.questions || [];
  const totalQuestions = questions.length;
  const quizContainerRef = useRef(null);

  useEffect(() => {
    // Start timing for the first question
    setQuestionStartTime(Date.now());
  }, []);

  useEffect(() => {
    // Track time spent on each question when moving to next
    return () => {
      if (questionStartTime) {
        const timeOnQuestion = Math.floor((Date.now() - questionStartTime) / 1000);
        setTimeSpent(prev => ({
          ...prev,
          [currentQuestion]: (prev[currentQuestion] || 0) + timeOnQuestion
        }));
      }
    };
  }, [currentQuestion, questionStartTime]);

  if (!quizData || !questions.length) {
    return (
      <div className="training-quiz">
        <div className="quiz-error">
          <h3>Quiz Not Available</h3>
          <p>This module doesn't have a quiz configured.</p>
        </div>
      </div>
    );
  }

  const handleAnswerChange = (questionIndex, answer) => {
    setResponses(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };

  const toggleMultiSelectOption = (questionIndex, optionIndex) => {
    setResponses(prev => {
      const current = Array.isArray(prev[questionIndex]) ? prev[questionIndex] : [];
      const exists = current.includes(optionIndex);
      const updated = exists ? current.filter(i => i !== optionIndex) : [...current, optionIndex];
      return {
        ...prev,
        [questionIndex]: updated
      };
    });
  };

  const goToQuestion = (index) => {
    // Record time spent on current question
    const timeOnQuestion = Math.floor((Date.now() - questionStartTime) / 1000);
    setTimeSpent(prev => ({
      ...prev,
      [currentQuestion]: (prev[currentQuestion] || 0) + timeOnQuestion
    }));
    
    setCurrentQuestion(index);
    setQuestionStartTime(Date.now());
    
    // Scroll to top of quiz container smoothly
    if (quizContainerRef.current) {
      quizContainerRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  const nextQuestion = () => {
    if (currentQuestion < totalQuestions - 1) {
      goToQuestion(currentQuestion + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      goToQuestion(currentQuestion - 1);
    }
  };

  const submitQuiz = async () => {
    setIsSubmitting(true);
    
    // Record final question time
    const finalTimeOnQuestion = Math.floor((Date.now() - questionStartTime) / 1000);
    const finalTimeSpent = {
      ...timeSpent,
      [currentQuestion]: (timeSpent[currentQuestion] || 0) + finalTimeOnQuestion
    };

    // Format responses for submission
    const formattedResponses = questions.map((question, index) => {
      let answer = responses[index];
      // Normalize answer formats per backend spec
      if (question.type === 'multiple_choice') {
        if (question.multi_select) {
          // Ensure array of unique numbers
          if (!Array.isArray(answer)) answer = [];
          answer = Array.from(new Set(answer.filter(a => typeof a === 'number')));
        } else if (typeof answer !== 'number') {
          // Single-select should be a number index; if absent leave undefined
          answer = typeof answer === 'string' ? parseInt(answer, 10) : answer;
        }
      } else if (question.type === 'true_false') {
        if (answer === 'true') answer = true;
        if (answer === 'false') answer = false;
      } else if (question.type === 'fill_blank' || question.type === 'short_answer' || question.type === 'essay') {
        if (typeof answer === 'string') answer = answer.trim();
      }
      return {
        question_id: question.id || index,
        answer,
        time_spent: finalTimeSpent[index] || 0
      };
    });

    try {
      const result = await onQuizSubmit({
        module_id: moduleId,
        responses: formattedResponses
      });

      setQuizResults(result);
      setShowResults(true);
      
      if (result.passed && onQuizComplete) {
        onQuizComplete(result);
      }
    } catch (error) {
      console.error('Quiz submission error:', error);
      alert('Quiz submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isQuestionAnswered = (question, answer) => {
    if (!question) return false;
    if (question.type === 'multiple_choice' && question.multi_select) {
      return Array.isArray(answer) && answer.length > 0;
    }
    return answer !== undefined; // Accept 0 as valid index for single-select
  };

  const isQuizComplete = () => {
    return questions.every((q, index) => isQuestionAnswered(q, responses[index]));
  };

  const renderQuestion = (question, index) => {
    const userAnswer = responses[index];

    switch (question.type) {
      case 'multiple_choice':
        if (question.multi_select) {
          const selected = Array.isArray(userAnswer) ? userAnswer : [];
          return (
            <div className="question-content">
              <h3 className="question-text">{question.question}</h3>
              <div className="multi-select-hint">Select all that apply.</div>
              <div className="options-container multi-select">
                {question.options.map((option, optionIndex) => {
                  const isChecked = selected.includes(optionIndex);
                  return (
                    <label key={optionIndex} className={`option-label ${isChecked ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        name={`question-${index}-option-${optionIndex}`}
                        value={optionIndex}
                        checked={isChecked}
                        onChange={() => toggleMultiSelectOption(index, optionIndex)}
                      />
                      <span className="option-text">{option}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        }
        return (
          <div className="question-content">
            <h3 className="question-text">{question.question}</h3>
            <div className="options-container">
              {question.options.map((option, optionIndex) => (
                <label key={optionIndex} className="option-label">
                  <input
                    type="radio"
                    name={`question-${index}`}
                    value={optionIndex}
                    checked={userAnswer === optionIndex}
                    onChange={(e) => handleAnswerChange(index, parseInt(e.target.value))}
                  />
                  <span className="option-text">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );
      case 'essay':
        return (
          <div className="question-content">
            <h3 className="question-text">{question.question}</h3>
            <div className="short-answer-container">
              <textarea
                className="short-answer-textarea"
                value={userAnswer || ''}
                onChange={(e) => handleAnswerChange(index, e.target.value)}
                placeholder="Type your answer here..."
                rows={6}
              />
            </div>
          </div>
        );

      case 'true_false':
        return (
          <div className="question-content">
            <h3 className="question-text">{question.question}</h3>
            <div className="options-container">
              <label className="option-label">
                <input
                  type="radio"
                  name={`question-${index}`}
                  value="true"
                  checked={userAnswer === 'true'}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                />
                <span className="option-text">True</span>
              </label>
              <label className="option-label">
                <input
                  type="radio"
                  name={`question-${index}`}
                  value="false"
                  checked={userAnswer === 'false'}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                />
                <span className="option-text">False</span>
              </label>
            </div>
          </div>
        );

      case 'fill_blank':
        return (
          <div className="question-content">
            <h3 className="question-text">{question.question}</h3>
            <div className="fill-blank-container">
              <input
                type="text"
                className="fill-blank-input"
                value={userAnswer || ''}
                onChange={(e) => handleAnswerChange(index, e.target.value)}
                placeholder="Type your answer here..."
              />
            </div>
          </div>
        );

      case 'short_answer':
        return (
          <div className="question-content">
            <h3 className="question-text">{question.question}</h3>
            <div className="short-answer-container">
              <textarea
                className="short-answer-textarea"
                value={userAnswer || ''}
                onChange={(e) => handleAnswerChange(index, e.target.value)}
                placeholder="Type your answer here..."
                rows={4}
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="question-content">
            <h3 className="question-text">{question.question}</h3>
            <p className="question-error">Unknown question type: {question.type}</p>
          </div>
        );
    }
  };

  const renderResults = () => {
    if (!quizResults) return null;

    const { score, correct_answers, total_questions, passed, passing_score, detailed_results } = quizResults;

    const indexToLetter = (i) => {
      if (typeof i !== 'number' || i < 0) return '';
      // Support beyond Z -> AA, AB if ever needed
      let n = i;
      let letters = '';
      do {
        letters = String.fromCharCode(65 + (n % 26)) + letters;
        n = Math.floor(n / 26) - 1;
      } while (n >= 0);
      return letters;
    };

    const formatMultiSelect = (raw, question) => {
      if (!question) return raw;
      // Already array of indices
      if (Array.isArray(raw)) {
        const nums = raw.map(v => (typeof v === 'string' ? parseInt(v,10) : v)).filter(v => !isNaN(v));
        return nums.map(indexToLetter).join(', ');
      }
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        // Comma/semicolon separated indices or letters
        if (/[;,]/.test(trimmed)) {
          return trimmed.split(/[;,]/).map(s => s.trim()).filter(Boolean).map(s => {
            if (/^\d+$/.test(s)) return indexToLetter(parseInt(s,10));
            return s; // already maybe a letter
          }).join(', ');
        }
        // JSON array
        if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('"[') && trimmed.endsWith(']"'))) {
          try {
            const parsed = JSON.parse(trimmed.replace(/^"|"$/g, ''));
            if (Array.isArray(parsed)) return parsed.map(v => indexToLetter(typeof v === 'string'? parseInt(v,10): v)).join(', ');
          } catch(_) {}
        }
        // Concatenated digits e.g. 03 -> indices 0 & 3 (heuristic)
        if (/^\d+$/.test(trimmed)) {
          if (trimmed.length === 1) return indexToLetter(parseInt(trimmed,10));
          const digits = trimmed.split('').map(d => parseInt(d,10));
          return Array.from(new Set(digits)).map(indexToLetter).join(', ');
        }
        return trimmed; // fallback
      }
      if (typeof raw === 'number') return indexToLetter(raw);
      return raw;
    };

    const formatAnswer = (result, index) => {
      const question = questions[index];
      if (question?.type === 'multiple_choice') {
        if (question.multi_select) return formatMultiSelect(result.user_answer, question) || '—';
        // single-select numeric index -> letter
        if (typeof result.user_answer === 'number') return indexToLetter(result.user_answer);
        if (typeof result.user_answer === 'string' && /^\d+$/.test(result.user_answer)) return indexToLetter(parseInt(result.user_answer,10));
      }
      if (typeof result.user_answer === 'boolean') return result.user_answer ? 'True' : 'False';
      return (result.user_answer === undefined || result.user_answer === null || result.user_answer === '') ? '—' : result.user_answer;
    };

    const formatCorrectAnswer = (result, index) => {
      const question = questions[index];
      if (!result.correct_answer) return null;
      if (question?.type === 'multiple_choice') {
        if (question.multi_select) return formatMultiSelect(result.correct_answer, question) || result.correct_answer;
        if (typeof result.correct_answer === 'number') return indexToLetter(result.correct_answer);
        if (typeof result.correct_answer === 'string' && /^\d+$/.test(result.correct_answer)) return indexToLetter(parseInt(result.correct_answer,10));
      }
      if (typeof result.correct_answer === 'boolean') return result.correct_answer ? 'True' : 'False';
      return result.correct_answer;
    };

    return (
      <div className="quiz-results">
        <div className={`results-header ${passed ? 'passed' : 'failed'}`}>
          <h2>{passed ? '🎉 Congratulations!' : '📚 Keep Learning!'}</h2>
          <div className="score-display">
            <span className="score-percentage">{Math.round(score)}%</span>
            <span className="score-fraction">({correct_answers}/{total_questions})</span>
          </div>
          <p className="score-status">
            {passed 
              ? `You passed! You scored ${Math.round(score)}% (passing score: ${passing_score}%)`
              : `You need ${passing_score}% to pass. You can retake the quiz to improve your score.`
            }
          </p>
        </div>

        <div className="detailed-results">
          <h3>Question Review</h3>
          {detailed_results.map((result, index) => {
            const formattedUser = formatAnswer(result, index);
            const formattedCorrect = !result.is_correct ? formatCorrectAnswer(result, index) : null;
            return (
              <div key={index} className={`result-item ${result.is_correct ? 'correct' : 'incorrect'}`}>
                <div className="result-header">
                  <span className="question-number">Question {index + 1}</span>
                  <span className={`result-indicator ${result.is_correct ? 'correct' : 'incorrect'}`}>
                    {result.is_correct ? '✓' : '✗'}
                  </span>
                </div>
                <p className="result-question">{result.question}</p>
                <div className="result-answers">
                  <div className="your-answer">
                    <strong>Your answer:</strong> {formattedUser}
                  </div>
                  {formattedCorrect && (
                    <div className="correct-answer">
                      <strong>Correct answer:</strong> {formattedCorrect}
                    </div>
                  )}
                  {result.explanation && (
                    <div className="explanation">
                      <strong>Explanation:</strong> {result.explanation}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="results-actions">
          {!passed && (
            <button 
              className={`${util.btn} ${util['btn--primaryGradient']} ${util.elevateHover}`}
              onClick={() => {
                setShowResults(false);
                setCurrentQuestion(0);
                setResponses({});
                setTimeSpent({});
                setStartTime(Date.now());
                setQuestionStartTime(Date.now());
              }}
            >
              Retake Quiz
            </button>
          )}
          <button 
            className={`${util.btn} ${util['btn--ghost']}`}
            onClick={() => onQuizComplete && onQuizComplete(quizResults)}
          >
            Back to Module
          </button>
        </div>
      </div>
    );
  };

  if (showResults) {
    return (
      <div className="training-quiz">
        <div className="quiz-container">
          {renderResults()}
        </div>
      </div>
    );
  }

  const currentQuestionData = questions[currentQuestion];
  const currentAnswered = isQuestionAnswered(currentQuestionData, responses[currentQuestion]);
  const progress_percentage = ((currentQuestion + 1) / totalQuestions) * 100;

  return (
    <div className="training-quiz">
      <div className="quiz-container" ref={quizContainerRef}>
        {/* Quiz Header */}
        <div className="quiz-header">
          <div className="quiz-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress_percentage}%` }}
              ></div>
            </div>
            <span className="quiz-progress-text">
              Question {currentQuestion + 1} of {totalQuestions}
            </span>
          </div>
          
          {progress?.quiz_attempts > 0 && (
            <div className="attempt-info">
              <span>Attempt #{progress.quiz_attempts + 1}</span>
              {progress.quiz_best_score && (
                <span>Best Score: {Math.round(progress.quiz_best_score)}%</span>
              )}
            </div>
          )}
        </div>

        {/* Question */}
        <div className="quiz-question">
          {renderQuestion(currentQuestionData, currentQuestion)}
        </div>

        {/* Navigation */}
        <div className="quiz-navigation">
          <div className="nav-buttons">
            <button 
              className={`${util.btn} ${util['btn--ghost']}`}
              onClick={prevQuestion}
              disabled={currentQuestion === 0}
            >
              ← Previous
            </button>
            
            {currentQuestion < totalQuestions - 1 ? (
              <button 
                className={`${util.btn} ${util['btn--primaryGradient']} ${util.elevateHover}`}
                onClick={nextQuestion}
                disabled={!currentAnswered}
              >
                Next →
              </button>
            ) : (
              <button 
                className={`${util.btn} ${util['btn--successGradient']} ${util.elevateHover}`}
                onClick={submitQuiz}
                disabled={!isQuizComplete() || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Quiz'}
              </button>
            )}
          </div>
          
          {/* Question Navigation Dots */}
          <div className="question-dots">
            {questions.map((_, index) => {
              const isCurrent = index === currentQuestion;
              const isAnswered = responses[index] !== undefined;
              const extra = [
                isCurrent ? util['btn-circle--current'] : '',
                isAnswered ? util['btn-circle--answered'] : ''
              ].filter(Boolean).join(' ');
              return (
                <button
                  key={index}
                  className={`${util['btn-circle']} ${extra}`}
                  onClick={() => goToQuestion(index)}
                  title={`Question ${index + 1}${isAnswered ? ' (answered)' : ''}`}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
        </div>

        {/* Quiz Instructions */}
        <div className="quiz-instructions">
          <p>
            <strong>Instructions:</strong> Answer all questions and click "Submit Quiz" when complete. 
            You need {quizData.passing_score || 80}% to pass this quiz.
          </p>
          {progress?.quiz_attempts > 0 && (
            <p className="retake-info">
              You can retake this quiz to improve your score. Your best score will be recorded.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModuleQuiz;